import { createFileRoute } from "@tanstack/react-router"
import { useAuth } from "../../hooks/useAuth"
import { useTenant } from "../../lib/tenant"
import { isSearchChannel, type TSearchChannel } from "../../lib/types/search"
import { CompaniesPage } from "./companies/-Companies"
import { MeetingListPage } from "./meetings/-MeetingList"

/** Dashboard omnisearch state, mirrored into the URL so refresh / back / share
 * keep the query + filters. `focusSearch` is a one-shot flag the wrapper
 * consumes to move focus into the box (e.g. from the Voxcanon header icon). */
export interface IDashboardSearch {
  q?: string
  channels?: TSearchChannel[]
  speaker?: string
  from?: string
  to?: string
  focusSearch?: boolean
}

function HomePage(): React.JSX.Element {
  const { user } = useAuth()
  const tenantState = useTenant()
  const isAdmin = tenantState.isAdmin && user?.isAdmin && !user?.activeOrganizationId

  if (isAdmin) {
    return <CompaniesPage />
  }

  return <MeetingListPage />
}

export const Route = createFileRoute("/_auth/")({
  component: HomePage,
  validateSearch: (search: Record<string, unknown>): IDashboardSearch => {
    const out: IDashboardSearch = {}
    if (typeof search.q === "string" && search.q.length > 0) out.q = search.q
    if (typeof search.speaker === "string" && search.speaker.length > 0) out.speaker = search.speaker
    if (typeof search.from === "string" && search.from.length > 0) out.from = search.from
    if (typeof search.to === "string" && search.to.length > 0) out.to = search.to
    if (search.focusSearch === true) out.focusSearch = true

    const raw = search.channels
    const tokens = Array.isArray(raw)
      ? raw.filter((c): c is string => typeof c === "string")
      : typeof raw === "string"
        ? raw.split(",")
        : []
    const channels = tokens.filter(isSearchChannel)
    if (channels.length > 0) out.channels = Array.from(new Set(channels))
    return out
  },
})
